import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import clsx from "clsx";
import { Icon } from "components/Icon";

/** Data younger than this reads as fresh (green); older is aging (yellow). */
export const FRESH_WINDOW_SECONDS = 20;

type TProps = {
  /** When data last arrived successfully — drives the dot color. */
  lastUpdated?: Date;
  /**
   * When a fetch last finished, successfully or not — drives the countdown.
   * The next poll is scheduled off this in useReportData, so counting down from
   * it is what makes the number match reality.
   */
  lastAttempt?: Date;
  refreshIntervalMs: number;
  /** A background poll is in flight right now. */
  isRefetching?: boolean;
};

/**
 * Live-data indicator: a blinking freshness dot plus a countdown to the next
 * refresh.
 *
 * This component owns the one-second ticker on purpose. Putting that state in
 * a report page would re-render its charts, grids and the OpenLayers map every
 * second; keeping it here means only these few nodes update.
 */
export function LiveIndicator({
  lastUpdated,
  lastAttempt,
  refreshIntervalMs,
  isRefetching,
}: TProps) {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!lastUpdated) return null;

  const secondsSinceUpdate = Math.max(
    0,
    Math.floor((now - lastUpdated.getTime()) / 1000)
  );
  const isFresh = secondsSinceUpdate < FRESH_WINDOW_SECONDS;

  const referenceMs = (lastAttempt ?? lastUpdated).getTime();
  const secondsUntilRefresh = Math.max(
    0,
    Math.ceil((referenceMs + refreshIntervalMs - now) / 1000)
  );

  const locale = i18n.language?.startsWith("fa") ? "fa-IR" : "en-US";
  const digits = (value: number, pad = 0) =>
    String(value)
      .padStart(pad, "0")
      .replace(/\d/g, (d) =>
        Number(d).toLocaleString(locale, { useGrouping: false })
      );

  const minutes = Math.floor(secondsUntilRefresh / 60);
  const seconds = secondsUntilRefresh % 60;
  /**
   * Always `m:ss`, never a bare seconds count. The countdown re-renders every
   * second, so a variable glyph count (9 → 45 → 4:12) would nudge everything
   * beside it on every tick.
   */
  const countdown = `${digits(minutes)}:${digits(seconds, 2)}`;

  return (
    <Tooltip
      arrow
      placement="bottom"
      title={
        <span className="flex flex-col gap-0.5">
          <span>
            {isRefetching
              ? t("syncingData")
              : `${t("nextRefresh")}: ${countdown}`}
          </span>
          <span>{isFresh ? t("liveDataFresh") : t("liveDataAging")}</span>
          {/* Composed by hand rather than with i18next interpolation/plurals:
              this app initialises i18next with no plural handling, so a
              `count` option would silently render the raw key. */}
          <span>{`${t("lastUpdate")}: ${digits(secondsSinceUpdate)} ${t(
            "secondsAgo"
          )}`}</span>
        </span>
      }
    >
      {/* Whole indicator is the hover target — the dot alone is an 8px box. */}
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <span
          aria-hidden="true"
          className={clsx(
            "inline-block h-2 w-2 shrink-0 animate-pulse rounded-full",
            isRefetching
              ? "bg-info-70"
              : isFresh
              ? "bg-success-70"
              : "bg-warning-70"
          )}
        />
        {/* Icon and number are one tight unit — the gap that separates the
            group from the dot should not also sit between them. */}
        <span className="flex items-center gap-0.5">
          {/* Spins while a background poll is in flight — the visible cue that
              an automatic refresh is happening. */}
          <Icon
            icon={isRefetching ? "MdSync" : "MdUpdate"}
            className={clsx(
              "text-sm text-gray-70",
              isRefetching && "animate-spin text-info-70"
            )}
          />
          {/* The countdown is always 4 glyphs (m:ss) and tabular-nums keeps
              them equal-width, so the min-width only has to cover faces
              without tabular Persian digits — hence just enough, not padding. */}
          <span className="inline-block min-w-[1.85rem] text-center text-tiny tabular-nums text-gray-90">
            {countdown}
          </span>
        </span>
      </span>
    </Tooltip>
  );
}

export default LiveIndicator;
